<template>
    <div>
        <loading-indicator v-if="loading || !entry"/>
        <form v-else ref="form">
            <form-row v-if="!isRequest" label="Abteilung" label-for="department" hide-validator-icon>
                <form-select name="department[]" v-model="entry.department" :options="entities" multiple>
                    <template #placeholder>
                        [Abteilung wählen]
                    </template>
                </form-select>
            </form-row>
            <form-row v-if="!isRequest" label="Mitarbeiter" label-for="employees">
                <form-select name="employees[]" v-model="entry.employees" :options="employeeOptions" multiple>
                    <template #placeholder>
                        [Mitarbeiter wählen]
                    </template>
                </form-select>
            </form-row>
            <div class="row">
                <div class="col-5">
                    <form-row class="form-row-checkbox" label="Ganztags" label-for="full_day">
                        <form-input-checkbox v-model="entry.full_day" :value-off="0" :value-on="1" class="toggle" name="full_day"/>
                    </form-row>
                </div>
            </div>
            <div class="row">
                <div class="col-6">
                    <form-row label="Beginn" error-name="from_date" label-for="from-date">
                        <form-input-date v-model="entry.from_date" name="from_date"/>
                    </form-row>
                </div>
                <div class="col-6">
                    <form-row label="Uhrzeit" error-name="from_time" label-for="from-time">
                        <form-input-date v-model="entry.from_time" name="from_time" type="time" :disabled="isTimeRequired"/>
                    </form-row>
                </div>
            </div>
            <div class="row">
                <div class="col-6">
                    <form-row label="Ende" error-name="to_date" label-for="to-date">
                        <form-input-date v-model="entry.to_date" name="to_date"/>
                    </form-row>
                </div>
                <div class="col-6">
                    <form-row label="Uhrzeit" error-name="to_time" label-for="to-time">
                        <form-input-date v-model="entry.to_time" name="to_time" type="time" :disabled="isTimeRequired"/>
                    </form-row>
                    <div v-if="isSpecialEdgeCase">
                        Dieser Abwesenheitseintrag wird als ganztägiger Eintrag am Endtag gespeichert.
                    </div>
                </div>
            </div>
            <div v-if="!isRequest" class="row">
                <div class="col-6">
                    <form-row label="Arbeitszeit abziehen"
                        help="Wenn Sie diese Option einschalten, wird die Zeit, in der dieser Mitarbeiter nicht arbeitet, von seiner normalen Wochenarbeitszeit abgezogen. Die Soll-Stunden des Mitarbeiters verringern sich also. Anwendungsbeispiele sind Urlaub oder Elternzeit.
                        In manchen Fällen bleibt die Wochenarbeitszeit gleich, auch wenn Mitarbeiter nicht anwesend sind. Anwendungsbeispiele für diesen Fall sind ein Sabbatical oder eine dienstliche Weiterbildung. In diesen Fällen deaktivieren Sie die Option, damit die Soll-Stunden des Mitarbeiters gleich bleiben.
                        Alle Abwesenheiten von Mitarbeitern werden auch zur Information von Kollegen in der Telefonleiste angezeigt."
                        label-for="work_time_substract" error-name="work_time_substract" class="form-row-checkbox">
                        <form-input-checkbox v-model="entry.work_time_substract" name="work_time_substract" :value-on="1" :value-off="0"/>
                    </form-row>
                </div>
            </div>
            <form-row v-if="isRequest" label="Begründung" label-for="request_reason">
                <form-textarea v-model="entry.request_reason" class="textarea-vertical" name="request_reason"/>
            </form-row>
            <form-row label="Grund" label-for="reason">
                <form-select v-model="entry.reason" name="reason" :options="reasonOptions"/>
            </form-row>
            <form-row v-if="reasonOther === entry.reason" v-model="entry.miscellaneous"
                label="Erklärung (Hinweis: dieser Text wird allen Mitarbeitern angezeigt!)" error-name="miscellaneous" label-for="miscellaneous">
                <form-input name="miscellaneous"/>
            </form-row>
        </form>
        <template v-if="representationWarning.colliding_own_absences && representationWarning.colliding_own_absences.length">
            <div class="error">
                <i class="material-icons md-light" aria-hidden="true" role="img">warning</i>
                <div>
                    Dieser Antrag kann nicht gespeichert werden, da für folgende Tage bereits Abwesenheiten im System erfasst wurden:
                    <ul>
                        <li v-for="absence of representationWarning.colliding_own_absences">{{absence}}</li>
                    </ul>
                </div>
            </div>
        </template>
        <template v-if="representationWarning.colliding_absences && representationWarning.colliding_absences.length">
            <div class="info">
                <i class="material-icons md-light" aria-hidden="true" role="img">info</i>
                <div>
                    Information: an folgenden Tagen liegen Kollisionen mit den Vertretungsreglungen aufgrund abwesender Mitarbeiter vor:
                    <ul>
                        <li v-for="absence of representationWarning.colliding_absences">{{absence}}</li>
                    </ul>
                </div>
            </div>
        </template>
        <template v-if="representationWarning.invalid_regulations && representationWarning.invalid_regulations.length">
            <div class="info">
                <i class="material-icons md-light" aria-hidden="true" role="img">info</i>
                <div>
                    Die Anzahl benötigter Mitarbeiter gleicht oder übersteigt die der Mitarbeiter in der Vertretungsregelung! Bitte überprüfen Sie die folgenden Vertretungsregelungen:
                    <ul>
                        <li v-for="regulation of representationWarning.invalid_regulations">{{regulation.title}}</li>
                    </ul>
                </div>
            </div>
        </template>
        <template v-if="representationWarning.colliding_regulations && representationWarning.colliding_regulations.length">
            <template v-for="regulation of representationWarning.colliding_regulations">
                <h2>{{regulation.title}}</h2>
                <absence-calendar :year="currentYear" :regulation-id="regulation.id" :ovr-id="id" :display-navigation="false" tag="div"/>
            </template>
        </template>
    </div>
</template>

<script lang="ts">
    import FormTextarea from '@/components/form/FormTextarea.vue';
    import Loader from '@/library/intranet/loader';
    import {NoOperation} from '@/library/utils';
    import dateHelper from '@/utils/portuna/dateHelper';
    import AbsenceCalendar from '@/views/absences/AbsenceCalendar.vue';
    import axios from 'axios';
    import {Component, Prop, Vue, Watch} from 'vue-facing-decorator';
    import FormInput from '../../components/form/FormInput.vue';
    import FormInputCheckbox from '../../components/form/FormInputCheckbox.vue';
    import FormInputDate from '../../components/form/FormInputDate.vue';
    import FormRow from '../../components/form/FormRow.vue';
    import FormSelect from '../../components/form/FormSelect.vue';
    import LoadingIndicator from '../../components/LoadingIndicator.vue';
    import {formSubmit, getFormData, parseFormData} from '../../library/form';
    import {ABSENCE_STATE_OPEN_REQUEST, REASON_OTHER} from '../../library/modules/absences/absences';
    import {Popup, popupDefaultCancelButton, popupDefaultCloseButton} from '../../library/popup';
    import TopMessageHandler from '../../library/topMessageHandler';
    import {errorHandler, XHR_STATUS_INVALID} from '../../utils/portuna';

    @Component({
        name: 'AbsenceEditPopup',
        components: {
            AbsenceCalendar,
            FormTextarea,
            FormInputDate,
            FormInputCheckbox,
            FormInput,
            FormSelect,
            FormRow,
            LoadingIndicator,
        },
        emits: ['setButtons', 'close', 'saved'],
    })
    export default class AbsenceEditPopup extends Vue
    {
        readonly $refs: {
            form: HTMLFormElement,
        };

        @Prop({
            type: Number,
            default: 0,
            required: false,
        }) id: number;

        @Prop({
            type: Boolean,
            default: false,
            required: false,
        }) request: boolean;

        entry: Nullable<IAbsenceEditPopupEntry> = null;

        assignments: Record<number, number[]> = {};

        entities: SelectOption[] = [];

        employees: SelectOption[] = [];

        reasons: SelectOption[] = [];

        loading: boolean = true;

        representationWarning: VacationRegulationWarning = {};

        reasonOther = REASON_OTHER;

        currentYear = dateHelper('Y') as number;

        previousData = {
            from_date: '',
            from_time: '',
            to_date: '',
            to_time: '',
            full_day: false,
        } as Record<'from_date' | 'from_time' | 'to_date' | 'to_time' | 'full_day', string | boolean>;

        @Watch('entry', {deep: true})
        watchEntry()
        {
            this.validate();
        }

        get isRequest()
        {
            return this.isRequestEdit || this.request;
        }

        get isRequestEdit()
        {
            return this.id !== 0 && this.entry && this.entry.state === ABSENCE_STATE_OPEN_REQUEST;
        }

        get employeeOptions()
        {
            if (this.entry && this.entry.department && 0 !== this.entry.department.length)
            {
                let employees: Record<string, string> = {};

                for (let employee of this.employees)
                {
                    if (this.assignments[employee.value])
                    {
                        let assignments = this.assignments[employee.value];

                        for (let entityId of assignments)
                        {
                            if (this.entry.department.includes(entityId))
                            {
                                employees[employee.value] = employee.label;
                            }
                        }
                    }
                }

                return employees;
            }

            return this.employees;
        }

        get isTimeRequired()
        {
            return !!(this.entry && this.entry.full_day);
        }

        get reasonOptions()
        {
            let options: SelectOption[] = [];

            for (let reason of this.reasons)
            {
                options.push(reason);
            }

            return options;
        }

        get xhrUrl()
        {
            return this.request ? `/employee/absences/request` : `/employee/absences/edit/id/${this.id}`;
        }

        get buttons()
        {
            const saveDisabled = !!(this.representationWarning.colliding_own_absences
                && this.representationWarning.colliding_own_absences.length);

            let buttonSave: PopupButton = {
                type: 'primary',
                title: 'Abwesenheit ' + (this.request ? 'beantragen' : 'hinzufügen'),
                icon: this.request ? 'assignment' : 'add',
                disabled: saveDisabled,
                tooltip: saveDisabled ? 'Da bereits eine eigene Abwesenheit in diesem Zeitraum vorliegt, kann kein Antrag erstellt werden' : '',
                cmd: () => this.submit(),
            };

            if (this.id)
            {
                buttonSave.type  = 'save';
                buttonSave.icon  = 'save';
                buttonSave.title = 'Abwesenheit speichern';
            }

            return [popupDefaultCancelButton, buttonSave];
        }

        get isSpecialEdgeCase()
        {
            return this.entry && this.entry.to_time === '00:00' && this.entry.from_time === '00:00';
        }

        created()
        {
            this.$emit('setButtons', this.buttons);
            this.fetch();
        }

        fetch()
        {
            this.axios.get(this.xhrUrl)
            .then(({data: {data: r}}) =>
            {
                this.$store.dispatch('form/setFilter', r.filter);
                this.entry       = r.entry;
                this.assignments = r.assignments;
                this.employees   = r.employees;
                this.entities    = r.entities;
                this.reasons     = r.reasons;
                this.loading     = false;
            })
            .catch(error =>
            {
                errorHandler(error);
                this.$emit('close');
            });
        }

        submit()
        {
            let formData = getFormData(this.$refs.form);

            formSubmit(this.xhrUrl, parseFormData(formData)).then(() =>
            {
                let action = this.request ? 'beantragt.' : 'hinzugefügt.';

                if (this.id)
                {
                    action = 'bearbeitet.';
                }

                TopMessageHandler.success('Die Abwesenheit wurde ' + action);

                if (!this.id && this.entry.work_time_substract && this.entry.from_date < dateHelper('Y-m-d'))
                {
                    let isSingleEmployee = this.entry.employees && this.entry.employees.length === 1;
                    let text = `Sie haben eine Abwesenheit mit Zeitabzug in der Vergangenheit ${this.request ? 'beantragt' : 'erstellt'}. ${this.request
                        ? 'Bei der Annahme des Antrags'
                        : 'Dadurch'} verändert sich automatisch ${this.request
                        ? 'Ihre Wochenauswertung und Ihr ggf. aktiviertes Stundenkonto wird neu berechnet'
                        : `die Wochenauswertung ${(isSingleEmployee
                            ? 'des Mitarbeiters und sein ggf. aktiviertes Stundenkonto wurde neu berechnet'
                            : 'der Mitarbeiter und deren ggf. aktivierten Stundenkonten wurden neu berechnet')}`}.`;

                    const popup = new Popup({
                        size: 'small',
                        title: 'Hinweis',
                        titleIcon: 'info',
                        textIcon: 'info',
                        textIconClass: 'text-grey',
                        text,
                        buttons: [popupDefaultCloseButton],
                    });

                    popup.open();
                }

                this.$emit('saved');
            })
            .catch(response =>
            {
                if (!this.request || typeof response.data === 'undefined' || response.data.status !== XHR_STATUS_INVALID)
                {
                    return;
                }

                this.representationWarning = response.data.data;
            });
        }

        validate()
        {
            if (!this.entry)
            {
                return;
            }

            if (!this.request)
            {
                return;
            }

            if (
                this.previousData.from_date === this.entry.from_date
                && this.previousData.from_time === this.entry.from_time
                && this.previousData.to_date === this.entry.to_date
                && this.previousData.to_time === this.entry.to_time
                && this.previousData.full_day === this.entry.full_day
            )
            {
                return;
            }

            this.previousData = {
                from_date: this.entry.from_date,
                from_time: this.entry.from_time,
                to_date: this.entry.to_date,
                to_time: this.entry.to_time,
                full_day: this.entry.full_day,
            };

            if (!this.entry.from_date || !this.entry.to_date)
            {
                return;
            }

            Loader.show('Bitte warten ...');

            axios
            .post(`/employee/absences/request-regulation-check`, parseFormData(this.entry))
            .then((response) =>
            {
                const {
                          status,
                          data,
                      } = response.data;

                if (status !== XHR_STATUS_INVALID)
                {
                    this.representationWarning = {};

                    return;
                }

                this.representationWarning = data;
            })
            .catch(NoOperation)
            .finally(() =>
            {
                Loader.hide();
                this.$emit('setButtons', this.buttons);
            });
        }
    }
</script>